/**
 * Chat update - pure state transition logic.
 * 
 * Elm-ish architecture: This function is pure.
 * - No API calls
 * - No storage access
 * - No hooks
 * - No side effects
 * - No mutation
 * 
 * Each message produces a new model and any effects that need to run.
 */
import type { ChatModel, ChatMessageDisplay } from './model';
import { fromRealtimeMessage } from './model';
import type { ChatMsg } from './messages';

/**
 * Effect types that can be triggered by state transitions.
 * These are handled by the effect runner (useChatProgram.ts).
 */
export type ChatEffect =
  /** Load the list of channels */
  | { readonly type: 'LoadChannels' }
  /**
   * CORRECTION01 (R2 fix): load a single channel's metadata (including
   * the S014 counterpart projection). Used on the channel-detail
   * branch so the ChatHeader can render name/age/city on the reload
   * path AND on the navigation path.
   */
  | { readonly type: 'LoadChannel'; readonly channelId: string }
  /** Load messages for a channel */
  | { readonly type: 'LoadMessages'; readonly channelId: string }
  /** Create a new channel */
  | { readonly type: 'CreateChannel' }
  /** Send a message to a channel */
  | { readonly type: 'SendMessage'; readonly channelId: string; readonly content: string };

/**
 * Result of an update call - new model plus effects to run.
 */
export interface UpdateResult {
  readonly model: ChatModel;
  readonly effects: readonly ChatEffect[];
}

/**
 * Pure update function - the heart of the Elm-ish architecture.
 * 
 * @param model - Current state
 * @param msg - Message triggering the transition
 * @returns New state and effects to run
 */
export function update(
  model: ChatModel,
  msg: ChatMsg
): UpdateResult {
  switch (msg.type) {
    // =========================================================================
    // Navigation / initialization
    // =========================================================================
    
    case 'Started':
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [{ type: 'LoadChannels' }],
      };

    case 'OpenChannel':
      return {
        model: {
          ...model,
          viewMode: 'channel-detail',
          selectedChannelId: msg.channelId,
          // CORRECTION01 (R2 fix): clear the selected channel until
          // the LoadChannel effect completes. The ChatHeader reads
          // selectedChannel.counterpart to render name/age/city.
          selectedChannel: null,
          messages: [],
          newMessageText: '',
          status: 'loading',
          error: null,
        },
        // CORRECTION01 (R2 fix): load the channel metadata (including
        // the S014 counterpart projection) so the ChatHeader can
        // render name/age/city on the reload path AND on the navigation
        // path. ListChannels already includes counterpart for the list
        // view, but reload (/chat/:id) needs this fetch.
        effects: [
          { type: 'LoadChannel', channelId: msg.channelId },
          { type: 'LoadMessages', channelId: msg.channelId },
        ],
      };

    case 'ChannelLoaded':
      // CORRECTION01 (R2 fix): the populated channel (with counterpart)
      // is stored in selectedChannel so the ChatHeader can render
      // the counterpart's name/age/city.
      return {
        model: {
          ...model,
          selectedChannel: msg.channel,
        },
        effects: [],
      };

    case 'ChannelLoadFailed':
      return {
        model: {
          ...model,
          // Leave selectedChannel as-is (null on first load, or last
          // valid value if a refresh failed). The ChatHeader will
          // render the "Conversation" placeholder.
          error: msg.error,
        },
        effects: [],
      };

    case 'BackToList':
      return {
        model: {
          ...model,
          viewMode: 'channel-list',
          selectedChannelId: null,
          // CORRECTION01 (R2 fix): clear the selected channel so the
          // header falls back to the "Conversation" placeholder /
          // channel-list view.
          selectedChannel: null,
          messages: [],
          newMessageText: '',
          status: 'idle',
          error: null,
        },
        effects: [],
      };

    // =========================================================================
    // Channel management
    // =========================================================================
    
    case 'ShowCreateForm':
      return {
        model: {
          ...model,
          showCreateForm: true,
          isCreating: false,
        },
        effects: [],
      };

    case 'HideCreateForm':
      return {
        model: {
          ...model,
          showCreateForm: false,
          isCreating: false,
        },
        effects: [],
      };

    case 'CreateChannel':
      return {
        model: {
          ...model,
          isCreating: true,
          showCreateForm: false,
          error: null,
        },
        effects: [{ type: 'CreateChannel' }],
      };

    case 'ChannelCreated':
      return {
        model: {
          ...model,
          isCreating: false,
          // Add new channel to the list
          channels: [msg.channel, ...model.channels],
        },
        effects: [],
      };

    case 'ChannelCreatedFailed':
      return {
        model: {
          ...model,
          isCreating: false,
          error: msg.error,
        },
        effects: [],
      };

    // =========================================================================
    // Channel loading
    // =========================================================================
    
    case 'ChannelsLoading':
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [],
      };

    case 'ChannelsLoaded':
      return {
        model: {
          ...model,
          status: 'loaded',
          channels: msg.channels,
          hasMoreChannels: msg.hasMore,
          error: null,
        },
        effects: [],
      };

    case 'ChannelsLoadFailed':
      return {
        model: {
          ...model,
          status: 'error',
          error: msg.error,
        },
        effects: [],
      };

    // =========================================================================
    // Message loading
    // =========================================================================
    
    case 'MessagesLoading':
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [],
      };

    case 'MessagesLoaded':
      return {
        model: {
          ...model,
          status: 'loaded',
          messages: msg.messages,
          hasMoreMessages: msg.hasMore,
          error: null,
        },
        effects: [],
      };

    case 'MessagesLoadFailed':
      return {
        model: {
          ...model,
          status: 'error',
          error: msg.error,
        },
        effects: [],
      };

    // =========================================================================
    // Message input
    // =========================================================================
    
    case 'NewMessageChanged':
      return {
        model: {
          ...model,
          newMessageText: msg.text,
        },
        effects: [],
      };

    // =========================================================================
    // Message sending
    // =========================================================================
    
    case 'SendMessage': {
      if (!model.selectedChannelId) {
        return { model, effects: [] };
      }
      
      const trimmedContent = model.newMessageText.trim();
      if (trimmedContent.length === 0) {
        return { model, effects: [] };
      }
      
      return {
        model: {
          ...model,
          newMessageText: '', // Clear input after send
        },
        effects: [{ type: 'SendMessage', channelId: model.selectedChannelId, content: trimmedContent }],
      };
    }

    case 'MessageSent':
      return {
        model: {
          ...model,
          // Prepend new message to list
          messages: [msg.message, ...model.messages],
          seenMessageIds: new Set(model.seenMessageIds).add(msg.message.id),
        },
        effects: [],
      };
    case 'MessageSendFailed':
      return {
        model: {
          ...model,
          // Restore message text so user can retry
          error: msg.error,
        },
        effects: [],
      };

    // =========================================================================
    // Error handling
    // =========================================================================

    case 'ClearError':
      return {
        model: {
          ...model,
          error: null,
        },
        effects: [],
      };

    case 'Retry':
      if (model.selectedChannelId) {
        return {
          model: {
            ...model,
            status: 'loading',
            error: null,
          },
          effects: [{ type: 'LoadMessages', channelId: model.selectedChannelId }],
        };
      }
      return {
        model: {
          ...model,
          status: 'loading',
          error: null,
        },
        effects: [{ type: 'LoadChannels' }],
      };

    // =========================================================================
    // Realtime (ACT-INDEEP-SPONSOR-FRIEND-PERSONAL-CHAT-REALTIME01)
    // =========================================================================

    case 'RealtimeStatusChanged':
      return {
        model: {
          ...model,
          realtimeStatus: msg.status,
        },
        effects: [],
      };

    case 'RealtimeMessageReceived': {
      if (model.selectedChannelId !== msg.message.channelId) {
        const nextSeen = new Set(model.seenMessageIds);
        nextSeen.add(msg.message.id);
        return {
          model: {
            ...model,
            seenMessageIds: nextSeen,
          },
          effects: [],
        };
      }
      if (model.seenMessageIds.has(msg.message.id)) {
        return {
          model,
          effects: [],
        };
      }
      const display: ChatMessageDisplay = fromRealtimeMessage(msg.message);
      const nextSeen2 = new Set(model.seenMessageIds);
      nextSeen2.add(msg.message.id);
      return {
        model: {
          ...model,
          messages: [display, ...model.messages],
          seenMessageIds: nextSeen2,
        },
        effects: [],
      };
    }

    case 'RealtimeMessageIgnored':
      return {
        model,
        effects: [],
      };

    // =========================================================================
    // Default (exhaustive check)
    // =========================================================================

    default:
      // Exhaustive check - should never reach here
      const _exhaustive: never = msg;
      return { model, effects: [] };
  }
}
